import React, { useContext, useEffect, useReducer, useState } from 'react';
import axios from 'axios';
import { Store } from '../Store';
import { getError } from '../utils';
import LoadingBox from '../components/LoadingBox';
import MessageBox from '../components/MessageBox';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Table from 'react-bootstrap/Table';
import Form from 'react-bootstrap/Form';
import { Helmet } from 'react-helmet-async';

const reducer = (state, action) => {
    switch (action.type) {
        case 'FETCH_REQUEST':
            return { ...state, loading: true };
        case 'FETCH_SUCCESS':
            return { ...state, data: action.payload, loading: false };
        case 'FETCH_FAIL':
            return { ...state, loading: false, error: action.payload };
        default:
            return state;
    }
};

export default function IncomeStatementScreen() {
    const [{ loading, error, data }, dispatch] = useReducer(reducer, {
        loading: true,
        error: '',
        data: {},
    });

    const { state } = useContext(Store);
    const { userInfo } = state;

    const [year, setYear] = useState(new Date().getFullYear());
    const [month, setMonth] = useState(new Date().getMonth() + 1);

    useEffect(() => {
        const fetchData = async () => {
            try {
                dispatch({ type: 'FETCH_REQUEST' });
                const { data } = await axios.get(
                    `/api/report/income-statement?year=${year}&month=${month}`,
                    { headers: { Authorization: `Bearer ${userInfo.token}` } }
                );
                dispatch({ type: 'FETCH_SUCCESS', payload: data });
            } catch (err) {
                dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
            }
        };
        fetchData();
    }, [userInfo, year, month]);

    const years = [];
    for (let i = 2023; i <= new Date().getFullYear(); i++) {
        years.push(i);
    }

    const months = [
        { name: 'Full Year', value: '' },
        { name: 'January', value: 1 },
        { name: 'February', value: 2 },
        { name: 'March', value: 3 },
        { name: 'April', value: 4 },
        { name: 'May', value: 5 },
        { name: 'June', value: 6 },
        { name: 'July', value: 7 },
        { name: 'August', value: 8 },
        { name: 'September', value: 9 },
        { name: 'October', value: 10 },
        { name: 'November', value: 11 },
        { name: 'December', value: 12 },
    ];

    return (
        <Container className="py-4">
            <Helmet>
                <title>Income Statement</title>
            </Helmet>
            <h1 className="mb-4">Income Statement</h1>

            <Card className="mb-4 border-0 shadow-sm">
                <Card.Body className="bg-light rounded">
                    <Row className="align-items-center">
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label className="fw-bold">Year</Form.Label>
                                <Form.Select value={year} onChange={(e) => setYear(Number(e.target.value))}>
                                    {years.map((y) => (
                                        <option key={y} value={y}>{y}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label className="fw-bold">Month</Form.Label>
                                <Form.Select value={month} onChange={(e) => setMonth(e.target.value)}>
                                    {months.map((m) => (
                                        <option key={m.value} value={m.value}>{m.name}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col className="text-end text-muted mt-4 mt-md-0">
                            <small>Period: {month ? months.find(m => m.value == month).name : 'Annual'} {year}</small>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {loading ? (
                <LoadingBox />
            ) : error ? (
                <MessageBox variant="danger">{error}</MessageBox>
            ) : (
                <Card className="shadow-sm border-0">
                    <Card.Body className="p-4 p-md-5">
                        <div className="text-center mb-5">
                            <h2 className="fw-bold text-uppercase">{userInfo.restaurantName || 'Business'} Statement of Income</h2>
                            <p className="text-muted">For the period ending {month ? months.find(m => m.value == month).name : 'December 31,'} {year}</p>
                        </div>

                        <Table borderless className="income-statement-table">
                            <tbody>
                                {/* Revenue Section */}
                                <tr>
                                    <td colSpan="2" className="fw-bold fs-5 border-bottom pb-2">Revenue</td>
                                </tr>
                                <tr>
                                    <td className="ps-4 py-3">Gross Sales</td>
                                    <td className="text-end py-3">{data.revenue.toLocaleString()} RWF</td>
                                </tr>
                                <tr className="bg-light fw-bold border-top border-bottom">
                                    <td className="ps-4">Total Revenue</td>
                                    <td className="text-end">{data.revenue.toLocaleString()} RWF</td>
                                </tr>

                                {/* COGS Section */}
                                <tr>
                                    <td colSpan="2" className="fw-bold fs-5 border-bottom pb-2 mt-4 d-block">Cost of Goods Sold</td>
                                </tr>
                                <tr>
                                    <td className="ps-4 py-3">Direct Production Costs</td>
                                    <td className="text-end py-3">({data.cogs.toLocaleString()}) RWF</td>
                                </tr>
                                <tr className="bg-light fw-bold border-top border-bottom">
                                    <td className="ps-4">Total COGS</td>
                                    <td className="text-end">({data.cogs.toLocaleString()}) RWF</td>
                                </tr>

                                <tr className="fs-4 fw-bold text-primary">
                                    <td className="py-4">GROSS PROFIT</td>
                                    <td className="text-end py-4">{data.grossProfit.toLocaleString()} RWF</td>
                                </tr>

                                {/* Expenses Section */}
                                <tr>
                                    <td colSpan="2" className="fw-bold fs-5 border-bottom pb-2 mt-4 d-block">Operating Expenses</td>
                                </tr>
                                {Object.entries(data.expenses).length > 0 ? (
                                    Object.entries(data.expenses).map(([category, amount]) => (
                                        <tr key={category}>
                                            <td className="ps-4 py-2">{category}</td>
                                            <td className="text-end py-2">{amount.toLocaleString()} RWF</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td className="ps-4 py-2 text-muted fst-italic">No expenses recorded for this period</td>
                                        <td className="text-end py-2">0 RWF</td>
                                    </tr>
                                )}
                                <tr className="bg-light fw-bold border-top border-bottom">
                                    <td className="ps-4">Total Operating Expenses</td>
                                    <td className="text-end">({data.totalOperatingExpenses.toLocaleString()}) RWF</td>
                                </tr>

                                <tr className="fs-3 fw-bold bg-dark text-white rounded">
                                    <td className="py-4 px-3">NET INCOME</td>
                                    <td className="text-end py-4 px-3">{data.netIncome.toLocaleString()} RWF</td>
                                </tr>
                            </tbody>
                        </Table>

                        <div className="mt-5 text-center text-muted small border-top pt-3">
                            <p>Confidential Financial Document - Internal Use Only</p>
                            <p>Generated on {new Date().toLocaleDateString()}</p>
                        </div>
                    </Card.Body>
                </Card>
            )}
        </Container>
    );
}
